/**
 * The simulation store: one small in-memory object, persisted to localStorage
 * (key `ll-sim-v1`) so a client demo survives reloads — posts written, cart,
 * tasks done, dismissed cards, seen-tour flag, scenario.
 *
 * Every storage access is wrapped in try/catch: private windows and blocked
 * storage just fall back to memory. Outside demo mode nothing is persisted.
 */
import { useSyncExternalStore } from "react";
import type { SimFeatureId } from "./flags";
import type { SimContextState, SimPersona, SimRegion, SimScenario } from "./types";
import { IS_SHOTS, SHOTS_DATE, SIM_PARAMS } from "./params";
import { PERSONAS, REGIONS, SCENARIOS } from "./vocab";

const STORAGE_KEY = "ll-sim-v1";
// 2: the context gained `market` (Oct 22 scope change), so older stores start fresh.
const STATE_VERSION = 2;
const PERSIST = Boolean(import.meta.env.VITE_DEMO_MODE);

export interface SimState {
  version: number;
  context: SimContextState;
  /** Sim Panel switches layered over VITE_SIM_FEATURES. */
  featureOverrides: Partial<Record<SimFeatureId, boolean>>;
  /** Each feature owns exactly one slice (its mock data and UI memory). */
  slices: Partial<Record<SimFeatureId, unknown>>;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function freshState(): SimState {
  return {
    version: STATE_VERSION,
    context: {
      scenario: "normal",
      simDate: IS_SHOTS ? SHOTS_DATE : todayIso(),
      market: "main",
      region: "Metro Manila",
      persona: "firstWeek",
    },
    featureOverrides: {},
    slices: {},
  };
}

function readPersisted(): SimState | null {
  if (!PERSIST || SIM_PARAMS.get("reset") === "1") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SimState;
    return parsed?.version === STATE_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

function parseRegion(raw: string | null): SimRegion | null {
  if (!raw) return null;
  const wanted = raw.trim().toLowerCase();
  return REGIONS.find((r) => r.id.toLowerCase() === wanted || r.short === wanted)?.id ?? null;
}

/**
 * URL parameters win over whatever was persisted (see params.ts). Demo mode
 * only: a normal build never lets a query string change what a family sees.
 */
function withBootParams(s: SimState): SimState {
  if (!PERSIST) return s;
  const context = { ...s.context };
  const market = SIM_PARAMS.get("market");
  if (market === "main" || market === "ph") context.market = market;
  const scenario = SIM_PARAMS.get("scenario");
  if (SCENARIOS.some((x) => x.id === scenario)) context.scenario = scenario as SimScenario;
  const region = parseRegion(SIM_PARAMS.get("region"));
  if (region) context.region = region;
  const persona = SIM_PARAMS.get("persona");
  if (PERSONAS.some((x) => x.id === persona)) context.persona = persona as SimPersona;
  const simDate = SIM_PARAMS.get("simDate");
  if (simDate && /^\d{4}-\d{2}-\d{2}$/.test(simDate)) context.simDate = simDate;
  else if (IS_SHOTS) context.simDate = SHOTS_DATE;
  return { ...s, context };
}

let state: SimState = withBootParams(readPersisted() ?? freshState());
const listeners = new Set<() => void>();

/**
 * Drop one-shot boot parameters from the address bar once applied, so a reload
 * mid-demo keeps what the presenter did instead of wiping it again. (Feature
 * parameters such as bundle=… are each feature's own business.)
 */
export function consumeBootParams(...names: string[]): void {
  if (!PERSIST) return;
  try {
    const url = new URL(window.location.href);
    if (!names.some((n) => url.searchParams.has(n))) return;
    names.forEach((n) => url.searchParams.delete(n));
    window.history.replaceState(window.history.state, "", url);
  } catch {
    // An unusual URL or a sandboxed history: leaving the parameter is harmless.
  }
}
consumeBootParams("reset");

function persist(): void {
  if (!PERSIST) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or blocked: the simulation keeps working in memory.
  }
}
persist();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getSimState(): SimState {
  return state;
}

export function setSimState(updater: (prev: SimState) => SimState): void {
  state = updater(state);
  persist();
  notify();
}

export function subscribeSim(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * React hook over the whole store. The selector must return a stable value —
 * a primitive or a reference that already lives in the state — never a fresh
 * object or array (that would re-render forever).
 */
export function useSimStore<T>(selector: (s: SimState) => T): T {
  return useSyncExternalStore(
    subscribeSim,
    () => selector(state),
    () => selector(state),
  );
}

/** Wipe everything (storage included). The Sim Panel reloads the page after. */
export function resetSimulation(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing persisted to remove.
  }
  state = freshState();
  persist();
  notify();
}

interface SliceEntry<T> {
  v: number;
  data: T;
}

export interface SliceHandle<T> {
  /** Current data — seeded on first use, and re-seeded when `version` changes. */
  read(): T;
  /** Replace the data immutably; returns the new value. */
  write(updater: (prev: T) => T): T;
  /**
   * React hook: re-renders when this slice changes. Same rule as useSimStore —
   * the selector returns a stable value (a primitive or a stored reference).
   */
  useSelect<R>(selector: (data: T) => R): R;
}

/**
 * A feature's own persisted slice. Bump `version` whenever the shape changes,
 * so stale demo data from an older build is re-seeded instead of crashing.
 */
export function defineSlice<T>(id: SimFeatureId, version: number, seed: () => T): SliceHandle<T> {
  const read = (): T => {
    const entry = state.slices[id] as SliceEntry<T> | undefined;
    if (entry && entry.v === version) return entry.data;
    const data = seed();
    state = { ...state, slices: { ...state.slices, [id]: { v: version, data } } };
    persist();
    return data;
  };
  return {
    read,
    write(updater) {
      const next = updater(read());
      setSimState((s) => ({ ...s, slices: { ...s.slices, [id]: { v: version, data: next } } }));
      return next;
    },
    useSelect(selector) {
      read();
      return useSimStore((s) => selector((s.slices[id] as SliceEntry<T>).data));
    },
  };
}
